/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { useCosmosComponentContext } from "@components/context";
import { CosmosButton } from "@components/elements";
import { withBasePath } from "@cosmos/common-utils";
import { IndustryFragment } from "@cosmos/hygraph-schema";
import { buildAuthRedirectUrl, useIsLoggedIn } from "@cosmos/next-hooks";
import { useLocation } from "@epic-mw/app-context";
import { useLocale, useMessages } from "@epic-mw/i18n";
import { useRouter } from "next/navigation";
import { Dispatch, SetStateAction, useCallback } from "react";

interface Props {
	selectedIndustry?: IndustryFragment | null;
	setSubmit: Dispatch<SetStateAction<boolean>>;
	disabled: boolean;
	isLoading: boolean;
	showIndustry: boolean;
}

export const NewsletterSignupButton = ({
	selectedIndustry,
	showIndustry,
	setSubmit,
	disabled,
	isLoading,
}: Props): React.JSX.Element => {
	const isLoggedIn = useIsLoggedIn();

	const router = useRouter();
	const location = useLocation();
	const locale = useLocale();
	const messages = useMessages();
	const { LoadingComponent, siteBasePath } = useCosmosComponentContext();
	const submitButtonLabel = messages["epic.cosmos.newsletter.subscribe"];
	const signInButtonLabel = messages["epic.cosmos.newsletter.signin"];
	const industryRequired = messages["epic.cosmos.newsletter.industry.required"] || "";

	const handleSubmit = useCallback(() => {
		if (isLoggedIn) {
			setSubmit(true);
		} else {
			const returnUrl = new URL(location);
			returnUrl.search = "";
			returnUrl.hash = "";
			returnUrl.searchParams.set("lang", locale);
			if (selectedIndustry?.salesForceId) {
				returnUrl.searchParams.set("subscribe", selectedIndustry?.salesForceId);
				returnUrl.hash = "newsletter-signup";
			}

			const authPath = withBasePath("/auth", siteBasePath ?? "");
			router.push(
				buildAuthRedirectUrl({
					authPath,
					redirectUrl: returnUrl.toString(),
					locale,
				}),
			);
		}
	}, [isLoggedIn, location, locale, router, selectedIndustry, setSubmit, siteBasePath]);

	if (isLoading && LoadingComponent) {
		return <LoadingComponent fill={false} />;
	}

	const label = isLoggedIn ? submitButtonLabel : signInButtonLabel;

	return (
		<CosmosButton
			disabled={disabled}
			title={disabled && showIndustry ? industryRequired : label}
			onClick={handleSubmit}
			variant="primary"
			label={label}
			size="lg"
		/>
	);
};
